import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {NgxSpinnerService} from "ngx-spinner";
import {
	AUTH_FORM_DROP_DOWN_STYLE,
	ERRORS,
	NOTIFICATION_KEYWORDS,
	REGEX,
	ROUTES,
	SIGN_UP_ERRORS,
	TAB_SET_STYLE,
	USER_TYPE
} from "../../../constants";
import {Subscription} from "rxjs";
import {AuthService} from "../../../services/auth.service";
import {I18nService} from "../../../services/i18n.service";
import {MessageService} from "../../../services/message.service";
import {NotificationService} from "../../../services/notification.service";
import {UtilityService} from "../../../services/utility.service";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {PasswordFieldComponent} from "../../../shared/password-field/password-field.component";
import { SessionService } from "../../../services/session.service";

@Component({
	selector: "app-signup",
	templateUrl: "./signup.component.html",
	styleUrls: ["./signup.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class SignupComponent implements OnInit, OnDestroy {
	validateRegisterForm: FormGroup;
	passwordValid: boolean = false;
	password: string = "";
	tabSetStyle = TAB_SET_STYLE;
	termsAndConditionsUrl: string;
	privacyPolicyUrl: string;
	subscription: Subscription;
	selectedUserType: string = USER_TYPE.BUYER;
	dropDownStyle = AUTH_FORM_DROP_DOWN_STYLE;
	showBuyerRegistrationButton: boolean = true;
	showSupplierRegistrationButton: boolean = true;
	invitedType: "buyer" | "supplier" | null = null;
	eventLinkCode: string;
	listOfOption = [
		{
			label: "global.user_type.buyer",
			value: "buyer"
		},
		{
			label: "global.user_type.supplier",
			value: "supplier"
		}
	];

	@ViewChild(PasswordFieldComponent) private passwordField: PasswordFieldComponent;

	constructor(
		private formBuilder: FormBuilder,
		private i18nService: I18nService,
		private authService: AuthService,
		private notificationService: NotificationService,
		private spinnerService: NgxSpinnerService,
		private utilityService: UtilityService,
		private messagesService: MessageService,
		private activatedRoute: ActivatedRoute,
		private sessionService: SessionService,
		private router: Router
	) {
		const urls = this.i18nService.getPrivacyPolicyAndTermsAndConditionsUrl();
		this.termsAndConditionsUrl = urls["termsAndConditions"];
		this.privacyPolicyUrl = urls["privacyPolicy"];
	}

	ngOnInit(): void {
		this.subscription = this.messagesService.getMessage().subscribe(response => {
			if (response.languageChange) {
				const urls = this.i18nService.getPrivacyPolicyAndTermsAndConditionsUrl();
				this.termsAndConditionsUrl = urls["termsAndConditions"];
				this.privacyPolicyUrl = urls["privacyPolicy"];
			}
		});

		this.validateRegisterForm = this.formBuilder.group({
			organization: [null, [Validators.required, Validators.minLength(3)]],
			firstName: ["",[Validators.required]],
			lastName: ["",[Validators.required]],
			email: [null, [Validators.required, Validators.email]],
			organizationType: [this.selectedUserType, [Validators.required]],
			website: ['']
		});

		this.activatedRoute.queryParams.subscribe((params) => {
			if (params.organization) {
				this.validateRegisterForm.controls["organization"].setValue(params.organization);
			}
			if (params.email) {
				this.validateRegisterForm.controls["email"].setValue(params.email);
				this.validateRegisterForm.controls["email"].disable();
			}
			if (params.firstName) {
				this.validateRegisterForm.controls["firstName"].setValue(params.firstName);
			}
			if (params.lastName) {
				this.validateRegisterForm.controls["lastName"].setValue(params.lastName);
			}
			if (params.website) {
				this.validateRegisterForm.controls["website"].setValue(params.website);
			}

			if (params.type){
				this.invitedType = params.type;
				if(params.type === "buyer"){
					this.showSupplierRegistrationButton = false;
				} else if (params.type === "supplier") {
					this.showBuyerRegistrationButton = false;
				}
			}

			this.selectedUserType = "supplier";
			if (params.type) {
				if (params.type === "buyer" || params.type === "supplier") {
					this.selectedUserType = params.type;
				}
			}

			if(params.code && params.type){
				const code = params.code;
				const type = params.type;
				this.spinnerService.show();
				this.authService.verifyLinkCode(code, type).subscribe(
					response => {
						this.eventLinkCode = code;
						this.spinnerService.hide();
					},
					error => {
						this.spinnerService.hide();
						console.error('Error', error);

						if (error.error.message === SIGN_UP_ERRORS.CODE_DOES_NOT_EXIST) {
							this.notificationService.failureNotification(SIGN_UP_ERRORS.CODE_DOES_NOT_EXIST);
						} else if (error.error.message === SIGN_UP_ERRORS.INVALID_CODE) {
							this.notificationService.failureNotification(SIGN_UP_ERRORS.INVALID_CODE);
						} else if (error.error.message === SIGN_UP_ERRORS.INACTIVE_CODE) {
							this.notificationService.failureNotification(SIGN_UP_ERRORS.INACTIVE_CODE);
						} else if (error.error.message === SIGN_UP_ERRORS.CODE_EXPIRED) {
							this.notificationService.failureNotification(SIGN_UP_ERRORS.CODE_EXPIRED);
						} if (error.error.message === SIGN_UP_ERRORS.CODE_NOT_VALID_FOR_BUYERS) {
							this.notificationService.failureNotification(SIGN_UP_ERRORS.CODE_NOT_VALID_FOR_BUYERS);
						}

						this.realoadNormalSignUpPage();
					}
				);
			}
		});

		this.formChanged();
	}

	ngOnDestroy() {
		this.formReset();
		this.subscription.unsubscribe();
	}

	/**
	 * Handle form reset event
	 */
	formReset(): void {
		this.validateRegisterForm.reset({organizationType: this.selectedUserType});
		this.passwordField.reset();
		return;
	}

	/**
	 * Detect the form field change
	 */
	formChanged(): void {
		this.validateRegisterForm.valueChanges.subscribe((value) => {
			const emailRegex = REGEX.EMAIL;
			const nameRegex = REGEX.NAME;
			const digitsRegex = REGEX.DIGITS;
			const usernameRegex = REGEX.USERNAME;
			const specialCharacterWithoutSpaceAndHyphen = REGEX.SPECIAL_CHARACTERS_WITHOUT_SPACE_HYPHEN;

			if(value.organization && value.organization.trim()===''){
				this.validateRegisterForm.controls["organization"].setErrors({"invalidOrganization": true});
			}

			if (value.firstName?.trim() && !value.firstName?.trim().match(usernameRegex)) {
				if (value.firstName && value.firstName?.trim().match(digitsRegex)) {
					this.validateRegisterForm.controls["firstName"].setErrors({"digitsNotAllowed": true});
				} else if (value.firstName && value.firstName.trim().match(specialCharacterWithoutSpaceAndHyphen)) {
					this.validateRegisterForm.controls["firstName"].setErrors({"invalidSpecialCharacter": true});
				} else {
					this.validateRegisterForm.controls["firstName"].setErrors({"invalidFirstName": true});
				}
			}

			if (value?.lastName?.trim() && !value?.lastName?.trim().match(usernameRegex)) {
				if (value?.lastName && value?.lastName?.trim().match(digitsRegex)) {
					this.validateRegisterForm.controls["lastName"].setErrors({"digitsNotAllowed": true});
				} else if (value?.lastName && value?.lastName?.trim().match(specialCharacterWithoutSpaceAndHyphen)) {
					this.validateRegisterForm.controls["lastName"].setErrors({"invalidSpecialCharacter": true});
				} else {
					this.validateRegisterForm.controls["lastName"].setErrors({"invalidLastName": true});
				}
			}

			if (value.email && value.email.endsWith("@")) {
				this.validateRegisterForm.controls["email"].setErrors({"incompleteEmail": true});
			} else if (value.email && !value.email.match(emailRegex)) {
				this.validateRegisterForm.controls["email"].setErrors({"invalidEmail": true});
			}
		});
	}

	/**
	 * Function for register form submission.
	 */
	submitRegisterForm(userType): void {
		for (const control in this.validateRegisterForm.controls) {
			this.validateRegisterForm.controls[control].markAsDirty();
			this.validateRegisterForm.controls[control].updateValueAndValidity();
		}

		const registerFormData = {
			organizationName: this.validateRegisterForm.controls['organization'].value?.trim(),
			firstName: this.validateRegisterForm.controls['firstName'].value?.trim(),
			lastName: this.validateRegisterForm.controls['lastName'].value?.trim(),
			email: this.validateRegisterForm.controls['email'].value,
			preferredLanguage: this.sessionService.language,
			password: this.password,
			website: this.validateRegisterForm.controls['website'].value,
		};

		if(this.eventLinkCode) {
			registerFormData['eventLinkCode'] = this.eventLinkCode;
		}

		this.spinnerService.show();
		this.authService.register(registerFormData, userType).subscribe(response => {

			if (response) {
				this.notificationService.successNotification(NOTIFICATION_KEYWORDS.SIGN_UP);
				this.spinnerService.hide();
				this.redirectToLoginPage();
			}
		}, (errorResponse) => {
			this.spinnerService.hide();
			if (errorResponse.error.statusCode === 409 && errorResponse.error.message === SIGN_UP_ERRORS.EMAIL_IN_USE) {
				this.notificationService.failureNotification(NOTIFICATION_KEYWORDS.EMAIL_ALREADY_EXISTS);
			} else if (errorResponse.error.statusCode === 409 && errorResponse.error.message === SIGN_UP_ERRORS.ORGANIZATION_IN_USE) {
				this.notificationService.failureNotification(NOTIFICATION_KEYWORDS.ORGANISATION_ALREADY_EXISTS);
			} else if (errorResponse.status === 403 && errorResponse.error.message === SIGN_UP_ERRORS.USER_NOT_ACTIVATED) {
				this.notificationService.failureNotification(NOTIFICATION_KEYWORDS.ACCOUNT_NOT_ACTIVATED);
			} else {
				this.notificationService.failureNotification(NOTIFICATION_KEYWORDS.SIGN_UP);
			}

			if (this.validateRegisterForm.value.email) {
				errorResponse.email = this.validateRegisterForm.value.email;
			}

			if (this.validateRegisterForm.value.organization?.trim()) {
				errorResponse.orgName = this.validateRegisterForm.value.organization?.trim();
			}

		});
	}

	realoadNormalSignUpPage() {
		const queryParams: Params = { tab: 'signup', type: null, code: null };

		this.router.navigate(
			[],
			{
				relativeTo: this.activatedRoute,
				queryParams: queryParams,
				queryParamsHandling: 'merge', // remove to replace all query params by provided
			});
	}

	/**
	 *	Change to specified language
	 * @param language : language string
	 */
	changeLanguage(language: string): void {
		this.i18nService.switchLanguage(language);
	}

	/**
	 * Navigate to register/signup page
	 */
	redirectToRegisterPage(): void {
		this.utilityService.navigate(ROUTES.SIGN_UP);
	}

	/**
	 * Navigate to login page
	 */
	redirectToLoginPage(): void {
		this.utilityService.navigate(ROUTES.LOG_IN);
	}

	changePasswordValid(event: boolean) {
		this.passwordValid = event;
	}
}
